// /catalog for Phoneware ops: Omni owns the products and writes each one to
// Autotask; ops link each to the OneBill product it bills on. The check card,
// the six-cell link strip, then Products, the Review queue and Not in Omni yet.
import React, { useEffect, useRef, useState } from "react";
import type { AutotaskAction, BillingLinkRequest, CatalogDismissal, CatalogPatchV2, ProductInput, SyncStatus } from "@phoneware/lens-domain";
import { ApiError } from "../../api.js";
import { Icon, type IconName } from "../../icons.js";
import { errorText, plural } from "../../format.js";
import { useLoad, useSession } from "../../session.js";
import { Callout, Head, ViewOnly, useToast } from "../../ui.js";
import { AddSheet } from "./AddSheet.js";
import { CheckCard } from "./CheckCard.js";
import { CatalogProvider, type CatalogContext, type Decision } from "./context.js";
import { DiffSheet } from "./DiffSheet.js";
import { atRef, blockers, kindWord, stripCounts, type AutotaskRow, type FilterId, type OneBillRow, type Product, type StripCounts } from "./model.js";
import { NotInOmni } from "./NotInOmni.js";
import { ProductSheet } from "./ProductSheet.js";
import { ProductsTab } from "./ProductsTab.js";
import { ReviewQueue } from "./ReviewQueue.js";

type Tab = "products" | "review" | "notin";
type OpenSheet = { kind: "product"; id: string } | { kind: "diffs"; id: string } | { kind: "add"; from: OneBillRow | null };

const STALE = "Someone changed this product since you opened it. Close it and open it again to see their change.";
const MIRRORED: Array<keyof CatalogPatchV2> = ["name", "description", "category", "listPrice", "unitCost", "active"];

function LinkStrip({ c, value, onChange }: { c: StripCounts; value: FilterId | ""; onChange: (f: FilterId | "") => void }): React.ReactElement {
  const cell = (id: FilterId, n: number, cls: string, icon: IconName, label: string, text: string) => (
    <button key={id} type="button" aria-pressed={value === id} data-testid={`filter-${id}`} onClick={() => onChange(value === id ? "" : id)}>
      <span className={n ? "n" : "n zero"}>{n.toLocaleString("en-US")}</span>
      <span className={`lbl ${cls}`}>
        <Icon name={icon} size={13} />
        {label}
      </span>
      <span>{text}</span>
    </button>
  );
  return (
    <div className="strip s6" role="group" aria-label="Filter by link state">
      {cell("quotable", c.quotable, "ok", "circle-check", "Quotable", c.offLinked ? `Switched on and linked in both. ${c.offLinked} more linked, switched off` : "Switched on and linked in both")}
      {cell("needs", c.needs, "warn", "triangle-alert", "Needs OneBill link", c.needs ? "In the review queue" : "Every product has a OneBill decision")}
      {cell("create", c.create, "az", "plus", "To create in OneBill", c.create ? "Omni creates them in OneBill on the next sync" : "Chosen in review when OneBill has no match")}
      {cell("notq", c.notq, "quiet", "ban", "Not quoted in Omni", "Ops decided OneBill doesn\u2019t bill these")}
      {cell(
        "changed",
        c.changed,
        c.missing ? "stop" : "warn",
        "arrow-right-left",
        "Changed in Autotask",
        c.changed ? `${c.edited} edited, ${c.missing} missing. Keep Omni\u2019s or take Autotask\u2019s` : "Nothing edited outside Omni",
      )}
      {cell("notat", c.notat, c.failed ? "stop" : "az", "clock", "Not in Autotask yet", c.notat ? `${c.creating} creating, ${c.failed} push failed` : "Everything Omni added is in Autotask")}
    </div>
  );
}

export function OpsCatalog(): React.ReactElement {
  const { api, can } = useSession();
  const toast = useToast();
  const links = can("catalog.link");
  const manages = can("catalog.manage");
  const catalog = useLoad<Product[]>(() => api.fullCatalog(), []);
  const status = useLoad<SyncStatus>(() => api.syncStatus(), []);
  const dismissals = useLoad<CatalogDismissal[]>(() => (links ? api.catalogDismissals() : Promise.resolve([])), [links]);
  const [tab, setTab] = useState<Tab>("products");
  const [filter, setFilter] = useState<FilterId | "">("");
  const [sheet, setSheet] = useState<OpenSheet | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [busyNote, setBusyNote] = useState(false);
  const list = catalog.data ?? [];
  // Actions finish after a render or two; they read the newest list, not the one they started with.
  const latest = useRef(list);
  latest.current = list;

  // While a check someone else started runs, look again every few seconds; when it ends, read the catalog it wrote.
  const inProgress = status.data?.inProgress ?? false;
  const wasInProgress = useRef(inProgress);
  useEffect(() => {
    if (wasInProgress.current && !inProgress) {
      setBusyNote(false);
      catalog.reload();
    }
    wasInProgress.current = inProgress;
    if (!inProgress) return;
    const timer = window.setTimeout(() => status.reload(), 4000);
    return () => window.clearTimeout(timer);
    // status.data changes on every read, which re-arms the timer.
  }, [inProgress, status.data]);

  function replace(p: Product): void {
    const now = latest.current;
    const next = now.some((x) => x.id === p.id) ? now.map((x) => (x.id === p.id ? p : x)) : [...now, p];
    latest.current = next;
    catalog.set(next);
  }

  async function attempt<T>(work: () => Promise<T>): Promise<T | null> {
    setBusy(true);
    setProblem(null);
    try {
      return await work();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setProblem(STALE);
        catalog.reload();
      } else setProblem(errorText(err));
      return null;
    } finally {
      setBusy(false);
    }
  }

  const ctx: CatalogContext = {
    links,
    manages,
    list,
    checked: status.data?.catalog ?? null,
    refused,
    problem,
    busy,
    reload: () => catalog.reload(),
    open: (id) => {
      setProblem(null);
      setRefused(null);
      setSheet({ kind: "product", id });
    },
    diffs: (id) => {
      setProblem(null);
      setSheet({ kind: "diffs", id });
    },
    add: (from) => {
      setProblem(null);
      setSheet({ kind: "add", from });
    },
    close: () => {
      setProblem(null);
      setSheet(null);
    },
    link: (p: Product, input: BillingLinkRequest, note: string) =>
      attempt(async () => {
        const saved = await api.setBillingLink(p.id, input);
        replace(saved);
        toast(note);
        return saved;
      }),
    decide: (p: Product, state: Decision) => {
      const note =
        state === "not_billed"
            ? `${p.name} is Not quoted in Omni.`
            : p.billingLink.via === "auto"
              ? `Moved ${p.name} back to the review queue. Omni won\u2019t link it automatically again.`
              : `${p.name} is back in the review queue.`;
      return ctx.link(p, { state }, note);
    },
    enable: async (p, on, from) => {
      if (on) {
        const reasons = blockers(p);
        if (reasons.length) {
          setRefused(p.id);
          if (from === "row") toast(`Can\u2019t switch on ${p.name}: ${reasons.join(", ")}.`);
          return;
        }
      }
      const saved = await attempt(() => api.patchCatalog(p.id, { enabled: on, expectedUpdatedAt: p.updatedAt }));
      if (!saved) return;
      replace(saved);
      setRefused(null);
      toast(on ? `Dealers can quote ${p.name} now.` : `${p.name} is hidden from dealers.`);
    },
    autotask: async (p: Product, action: AutotaskAction) => {
      const saved = await attempt(() => api.autotaskAction(p.id, { action }));
      if (!saved) return null;
      replace(saved);
      if (action === "keep_omni") toast(`Wrote Omni\u2019s values to ${atRef(saved)} and read it back.`);
      else if (action === "take_autotask") toast(`Updated Omni to match ${atRef(saved)}.`);
      else if (saved.autotaskSync.state === "in_sync") toast(`Created ${saved.name} as ${atRef(saved)} and read it back.`);
      else toast(`Autotask didn\u2019t take ${saved.name}. Omni kept it; Retry when Autotask is back.`);
      setSheet({ kind: "product", id: saved.id });
      return saved;
    },
    save: async (p, patch) => {
      const saved = await attempt(() => api.patchCatalog(p.id, { ...patch, expectedUpdatedAt: p.updatedAt }));
      if (!saved) return null;
      replace(saved);
      const mirrored = MIRRORED.filter((k) => k in patch).length;
      toast(
        mirrored && saved.autotask
          ? `Saved. Wrote ${plural(mirrored, "field")} to ${atRef(saved)} and read it back.`
          : mirrored
            ? "Saved in Omni. Autotask gets it once the product is there."
            : `Saved ${saved.name} in Omni.`,
      );
      return saved;
    },
  };

  async function create(input: ProductInput): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    try {
      const saved = await api.createProduct(input);
      replace(saved);
      toast(`Created ${saved.name} as ${atRef(saved)} and read it back.`);
      setTab("products");
      setFilter("");
      if (input.onebill) status.reload();
      return true;
    } catch (err) {
      const kept = err instanceof ApiError && err.status === 502 && err.body && typeof err.body === "object" && "product" in err.body ? (err.body.product as Product) : null;
      if (kept) {
        replace(kept);
        toast(`Autotask didn\u2019t take ${kept.name}. Omni kept it; Retry when Autotask is back.`);
        setTab("products");
        setFilter("notat");
        return true;
      }
      setProblem(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function adopt(row: AutotaskRow): Promise<void> {
    const saved = await attempt(() => api.adoptFromAutotask({ kind: row.kind, id: row.id }));
    if (!saved) return;
    replace(saved);
    toast(`Added ${row.name} to Omni from Autotask ${kindWord(saved)} ${row.id}. It needs a OneBill link.`);
    status.reload();
  }

  async function dismiss(source: "autotask" | "onebill", ref: string, name: string): Promise<void> {
    const saved = await attempt(() => api.dismissCatalogItem({ source, ref, name }));
    if (!saved) return;
    toast(source === "autotask" ? "Ignored. Omni leaves it in Autotask only." : `${name} is Not quoted in Omni.`);
    dismissals.reload();
    status.reload();
  }

  async function restore(items: CatalogDismissal[]): Promise<void> {
    const done = await attempt(async () => {
      for (const d of items) await api.restoreCatalogItem(`${d.source}:${d.ref}`);
      return true;
    });
    dismissals.reload();
    status.reload();
    if (done) toast(items.length === 1 ? `${items[0].name || items[0].ref} is back in Not in Omni yet.` : `${items.length} items are back in Not in Omni yet.`);
  }

  async function runCheck(): Promise<void> {
    setRunning(true);
    setBusyNote(false);
    setProblem(null);
    try {
      const run = await api.runSync({ steps: ["catalog"] });
      const step = run.steps.catalog;
      if (step?.outcome === "failed") toast("The check failed. Nothing was saved.");
      else toast(step && step.wrote.products !== undefined ? `Check finished. ${plural(step.wrote.products, "product")} updated.` : "Check finished.");
      catalog.reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setBusyNote(true);
      else setProblem(errorText(err));
    } finally {
      setRunning(false);
      status.reload();
    }
  }

  const checked = status.data?.catalog ?? null;
  const scores: Record<string, number> = {};
  for (const row of checked?.notInOmni.onebill ?? []) {
    for (const cand of row.candidates) scores[cand.productId] = Math.max(scores[cand.productId] ?? 0, cand.score);
  }
  const reviewCount = list.filter((p) => p.active && p.billingLink.state === "unreviewed").length;
  const notInCount = checked ? checked.notInOmni.onebill.length + checked.notInOmni.autotask.length : 0;
  const open = sheet && sheet.kind !== "add" ? list.find((p) => p.id === sheet.id) ?? null : null;
  const tabButton = (id: Tab, label: string, n: number) => (
    <button key={id} type="button" role="tab" aria-selected={tab === id} data-testid={`tab-${id}`} onClick={() => setTab(id)}>
      {label}
      <span className="cnt">{n.toLocaleString("en-US")}</span>
    </button>
  );

  return (
    <CatalogProvider value={ctx}>
      <div className="omni-cat" data-testid="ops-catalog">
        <Head
          title="Catalog"
          sub="Omni owns these products and writes each one to Autotask. Billing ops link each one to the OneBill product it bills on. Dealers quote only what is switched on and linked in both. Product codes are read from Autotask and KQM."
          actions={
            manages ? (
              <button className="btn primary" type="button" data-testid="add-product" onClick={() => ctx.add(null)}>
                <Icon name="plus" size={15} />
                Add product
              </button>
            ) : null
          }
        />
        {links || manages ? null : (
          <div className="gap-b">
            <ViewOnly testId="catalog-view-only">Billing ops link the catalog and Super admins change it. You can see every product, price and link.</ViewOnly>
          </div>
        )}
        {problem && !sheet ? (
          <div className="gap-b" data-testid="catalog-problem">
            <Callout kind="stop" icon="triangle-alert">
              {problem}
            </Callout>
          </div>
        ) : null}
        {catalog.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {catalog.error.message}
          </Callout>
        ) : !catalog.data ? (
          <section className="card">
            <div className="empty">Loading the catalog.</div>
          </section>
        ) : (
          <>
            <CheckCard
              status={status.data}
              statusError={status.error ? status.error.message : null}
              running={running}
              busyNote={busyNote}
              reviewCount={reviewCount}
              onCheck={can("sync.run") ? () => void runCheck() : null}
            />
            <LinkStrip
              c={stripCounts(list)}
              value={filter}
              onChange={(f) => {
                setFilter(f);
                setTab("products");
              }}
            />
            <div className="cat-tabs" role="tablist" aria-label="Catalog">
              {tabButton("products", "Products", list.filter((p) => p.active).length)}
              {tabButton("review", "Review queue", reviewCount)}
              {tabButton("notin", "Not in Omni yet", notInCount)}
            </div>
            {tab === "products" ? (
              <ProductsTab filter={filter} onFilter={setFilter} onReview={() => setTab("review")} />
            ) : tab === "review" ? (
              <ReviewQueue scores={scores} />
            ) : (
              <NotInOmni dismissals={dismissals.data ?? []} adopt={(row) => void adopt(row)} dismiss={(s, r, n) => void dismiss(s, r, n)} restore={(items) => void restore(items)} />
            )}
          </>
        )}
        {sheet?.kind === "product" && open ? <ProductSheet key={open.id} p={open} /> : null}
        {sheet?.kind === "diffs" && open ? <DiffSheet p={open} /> : null}
        {sheet?.kind === "add" ? <AddSheet from={sheet.from} list={list} checked={ctx.checked} busy={busy} problem={problem} onClose={ctx.close} create={create} /> : null}
      </div>
    </CatalogProvider>
  );
}
